/**
 * La escena: junta camara, nodos, enlaces e interaccion en un solo bucle.
 *
 * Reparto de trabajo por cuadro, que es lo que sostiene la fluidez:
 *
 *   - Mover el lienzo o el zoom NO recalcula posiciones ni toca la fisica.
 *     Solo cambia la transformacion del contenedor del mundo: una operacion.
 *   - Las posiciones se calculan una vez al cargar y se guardan. La fisica
 *     solo vuelve a correr si alguien arrastra un nodo o si cambian los datos,
 *     y se para sola cuando la energia baja del umbral.
 *   - Los enlaces se redibujan cuando cambian las posiciones, cuando cambia
 *     el resaltado o cuando el zoom varia lo suficiente para que el grosor
 *     compensado se note. No en cada cuadro.
 *   - El resaltado y la respiracion son solo opacidades.
 */
// Pixi genera codigo en caliente (con `new Function`) para sincronizar
// uniformes y lotes. Eso obliga a permitir `unsafe-eval` en la politica de
// contenido, y aqui no se quiere: la aplicacion declara que no carga ni
// ejecuta nada de fuera, y esa declaracion vale mas cuanto mas estricta sea.
// Este modulo instala las versiones sin `eval` de esas rutinas. Tiene que
// importarse ANTES de crear el renderizador.
import "pixi.js/unsafe-eval";
import { Application, Container, Graphics, Text } from "pixi.js";
import type { Arbol } from "../core/model.js";
import { nombreCompleto } from "../core/model.js";
import type { Indice } from "../core/grafo.js";
import { parentesco } from "../core/parentesco.js";
import { pasoDisposicion, type Disposicion } from "../layout/layout.js";
import { ALTO_FILA, ALTO_NODO, ANCHO_NODO } from "../layout/medidas.js";
import { Camara } from "./camara.js";
import { construirSegmentos, Enlaces } from "./enlaces.js";
import { detalleDe, LADO_PUNTO_PANTALLA, suave } from "./lod.js";
import { NodoVista } from "./nodos.js";
import {
  ALFA_ATENUADO,
  BANDA,
  BANDA_ALFA,
  FUENTE,
  LIENZO,
  REJILLA,
  REJILLA_ALFA,
  TEXTO_TENUE,
} from "./tema.js";
import { crearTexturas, type Texturas } from "./texturas.js";

/** Por debajo de esta energia la fisica se para y deja de gastar cuadros. */
const ENERGIA_PARADA = 0.03;
/** Suavizado del resaltado. */
const SUAVIZADO_ALFA = 0.18;
/** Amplitud de la respiracion en reposo. Muy leve a proposito. */
const RESPIRACION = 0.03;
/** Velocidad de la respiracion, en radianes por milisegundo. */
const RESPIRACION_VEL = 0.0011;

export interface Metricas {
  fps: number;
  nodos: number;
  /** Cuadros en los que hubo que redibujar enlaces, del ultimo segundo. */
  redibujosEnlaces: number;
  /** Si la fisica esta corriendo. */
  fisicaViva: boolean;
  escala: number;
}

export interface OpcionesEscena {
  contenedor: HTMLElement;
  arbol: Arbol;
  ix: Indice;
  disposicion: Disposicion;
  alPasar?: (handle: string | undefined) => void;
  alSeleccionar?: (handle: string, aditivo: boolean) => void;
  alFondo?: () => void;
  alMedir?: (m: Metricas) => void;
}

export class Escena {
  private app!: Application;
  private tex!: Texturas;
  private readonly mundo = new Container();
  private readonly capaGuias = new Graphics();
  private readonly capaNodos = new Container();
  private readonly capaEtiquetasFila = new Container();
  /** Etiquetas de generacion, que viven en coordenadas de pantalla. */
  private readonly etiquetasFila: Array<{ fila: number; caja: Container }> = [];
  private camara!: Camara;

  private arbol!: Arbol;
  private ix!: Indice;
  private disposicion!: Disposicion;
  private nodos = new Map<string, NodoVista>();
  private enlaces!: Enlaces;

  private fisicaViva = false;
  private resaltadoActual: string | undefined;
  /** Desde quien se leen los parentescos que salen bajo cada nombre. */
  private perspectiva: string | undefined;
  private seleccion = new Set<string>();
  private atenuacionActual = 1;
  private atenuacionObjetivo = 1;

  private tiempo = 0;
  private cuadros = 0;
  private redibujos = 0;
  private ultimaMedida = 0;

  private arrastrandoLienzo = false;
  private arrastrandoNodo: string | undefined;
  private ultimoPuntero = { x: 0, y: 0 };

  private op!: OpcionesEscena;

  static async crear(op: OpcionesEscena): Promise<Escena> {
    const e = new Escena();
    await e.inicializar(op);
    return e;
  }

  private async inicializar(op: OpcionesEscena): Promise<void> {
    this.op = op;
    this.app = new Application();
    await this.app.init({
      background: LIENZO,
      antialias: true,
      resizeTo: op.contenedor,
      // La resolucion del dispositivo, para que el texto no salga borroso en
      // pantallas densas.
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      autoDensity: true,
      preference: "webgl",
    });
    op.contenedor.appendChild(this.app.canvas);
    this.app.canvas.style.display = "block";
    this.app.canvas.style.touchAction = "none";

    this.tex = crearTexturas(this.app.renderer);
    this.camara = new Camara(this.app.screen.width, this.app.screen.height);

    this.mundo.addChild(this.capaGuias);
    this.app.stage.addChild(this.mundo);
    // Encima del mundo y SIN su transformacion: estas etiquetas se quedan
    // pegadas al borde de la pantalla.
    this.app.stage.addChild(this.capaEtiquetasFila);

    this.cargar(op.arbol, op.ix, op.disposicion);
    this.conectarEntrada();

    this.app.ticker.add(() => this.cuadro());
    window.addEventListener("resize", this.alRedimensionar);
  }

  // --- carga y recarga ----------------------------------------------------

  /** Monta (o vuelve a montar) todo el contenido a partir de una disposicion. */
  cargar(arbol: Arbol, ix: Indice, disposicion: Disposicion): void {
    this.arbol = arbol;
    this.ix = ix;
    this.disposicion = disposicion;

    for (const n of this.nodos.values()) n.destruir();
    this.nodos.clear();
    this.capaNodos.removeChildren();
    this.capaEtiquetasFila.removeChildren();
    if (this.enlaces) this.enlaces.destruir();

    const segmentos = construirSegmentos(ix, disposicion.nudos);
    this.enlaces = new Enlaces(segmentos, (h) => {
      const n = disposicion.fisica.nodos.get(h);
      return n ? { x: n.x, y: n.y } : undefined;
    });

    // Orden de capas: guias, enlaces normales, enlaces resaltados, nodos.
    this.mundo.removeChildren();
    this.mundo.addChild(
      this.capaGuias,
      this.enlaces.normales,
      this.enlaces.resaltados,
      this.capaNodos,
    );

    for (const [h, p] of arbol.personas) {
      const nodo = disposicion.fisica.nodos.get(h);
      if (!nodo) continue;
      const v = new NodoVista(h, p, this.tex);
      v.mover(nodo.x, nodo.y);
      this.nodos.set(h, v);
      this.capaNodos.addChild(v.raiz);
    }

    this.dibujarGuias();
    this.enlaces.invalidar();
    this.fisicaViva = false;

    // Si habia un punto de perspectiva y esa persona sigue existiendo, se
    // vuelven a calcular las etiquetas sobre el arbol nuevo.
    if (this.perspectiva && arbol.personas.has(this.perspectiva)) {
      this.ponerPerspectiva(this.perspectiva);
    } else {
      this.perspectiva = undefined;
    }
  }

  // --- punto de perspectiva ----------------------------------------------

  /**
   * Elige desde quien se lee el arbol.
   *
   * A partir de ese momento, debajo del nombre de cada persona aparece el
   * parentesco con ella ("padre", "prima hermana", "medio tio"). Quien no
   * tenga ningun parentesco no lleva etiqueta, que es lo correcto: una
   * etiqueta vacia o un "sin parentesco" en cada nodo solo seria ruido.
   *
   * Pasar `undefined` quita las etiquetas.
   */
  ponerPerspectiva(handle: string | undefined): {
    conParentesco: number;
    ms: number;
  } {
    this.perspectiva = handle && this.nodos.has(handle) ? handle : undefined;
    const t0 = performance.now();
    let conParentesco = 0;

    for (const [h, v] of this.nodos) {
      if (!this.perspectiva) {
        v.ponerRelacion("");
        continue;
      }
      if (h === this.perspectiva) {
        v.ponerRelacion("· punto de vista ·");
        continue;
      }
      const r = parentesco(this.ix, this.perspectiva, h);
      if (r.clase === "ninguno") {
        v.ponerRelacion("");
      } else {
        v.ponerRelacion(r.breve);
        conParentesco++;
      }
    }
    return { conParentesco, ms: performance.now() - t0 };
  }

  get perspectivaActual(): string | undefined {
    return this.perspectiva;
  }

  /** Encuadra todo el arbol en la vista. */
  encuadrarTodo(inmediato = false): void {
    const l = this.disposicion.fisica.limites();
    this.camara.encuadrar(
      {
        x0: l.x0 - ANCHO_NODO / 2,
        y0: l.y0 - ALTO_NODO,
        x1: l.x1 + ANCHO_NODO / 2,
        y1: l.y1 + ALTO_NODO * 2,
      },
      80,
      inmediato,
    );
  }

  /** Lleva la vista a una persona, sin cambiar el zoom. */
  irA(handle: string): void {
    const n = this.disposicion.fisica.nodos.get(handle);
    if (n) this.camara.centrarEn(n.x, n.y);
  }

  /** Fija el zoom, anclando en el centro de la vista. */
  ponerEscala(valor: number): void {
    this.camara.ponerEscala(valor);
  }

  get escalaActual(): number {
    return this.camara.escala;
  }

  // --- guias de generacion ------------------------------------------------

  /**
   * Bandas horizontales alternas y un eje por generacion.
   *
   * No son decoracion. Cuando se ve el arbol entero, las personas son puntos
   * de nueve pixeles y las filas quedan muy juntas en pantalla: la banda es
   * lo unico que sigue diciendo donde acaba una generacion y empieza la
   * siguiente. De cerca es casi invisible y no estorba.
   */
  private dibujarGuias(): void {
    this.capaGuias.clear();
    this.etiquetasFila.length = 0;
    this.capaEtiquetasFila.removeChildren();

    const l = this.disposicion.fisica.limites();
    const margen = ANCHO_NODO * 2;
    const x0 = l.x0 - margen;
    const x1 = l.x1 + margen;
    const max = this.disposicion.generaciones.maxFila;

    for (let f = 0; f <= max; f++) {
      const y = f * ALTO_FILA;

      if (f % 2 === 1) {
        this.capaGuias.rect(x0, y - ALTO_FILA / 2, x1 - x0, ALTO_FILA);
        this.capaGuias.fill({ color: BANDA, alpha: BANDA_ALFA });
      }
      this.capaGuias.moveTo(x0, y).lineTo(x1, y);
      this.capaGuias.stroke({ width: 1.2, color: REJILLA, alpha: REJILLA_ALFA });

      const cuantos = [...this.disposicion.fisica.nodos.values()].filter(
        (n) => n.fila === f,
      ).length;
      // La etiqueta va en coordenadas de PANTALLA, no del mundo: pegada al
      // borde izquierdo y siempre visible. En el mundo quedaba a miles de
      // unidades a la izquierda y, en cuanto uno se movia, desaparecia.
      const t = new Text({
        text: `generación ${f}  ·  ${cuantos} personas`,
        style: { fontFamily: FUENTE, fontSize: 12, fill: TEXTO_TENUE },
      });
      t.resolution = 2;
      t.position.set(9, 4);

      // Un fondo redondeado detras: sin el, al acercarse la etiqueta cae
      // encima de las tarjetas y las dos cosas se vuelven ilegibles. Con el
      // se lee como lo que es, un rotulo de la interfaz y no del diagrama.
      const fondo = new Graphics();
      fondo.roundRect(0, 0, t.width + 18, t.height + 8, 999);
      fondo.fill({ color: LIENZO, alpha: 0.82 });
      fondo.stroke({ width: 1, color: REJILLA, alpha: 0.9 });

      const caja = new Container();
      caja.addChild(fondo, t);
      caja.eventMode = "none";
      this.capaEtiquetasFila.addChild(caja);
      this.etiquetasFila.push({ fila: f, caja });
    }
  }

  /**
   * Recoloca las etiquetas de generacion contra el borde de la pantalla, y
   * las desvanece al acercarse.
   *
   * Sirven para saber que fila es cual cuando el arbol se ve entero y las
   * generaciones quedan aplastadas unas contra otras. De cerca ya se ve la
   * estructura y lo unico que hacen es taparle la tarjeta a alguien, asi que
   * se van.
   */
  private colocarEtiquetasFila(): void {
    const alto = this.app.screen.height;
    const opacidad = 1 - suave(0.22, 0.46, this.camara.escala);
    for (const { fila, caja } of this.etiquetasFila) {
      if (opacidad < 0.01) {
        caja.visible = false;
        continue;
      }
      const y = this.camara.aPantalla(0, fila * ALTO_FILA).y;
      const dentro = y > 20 && y < alto - 40;
      caja.visible = dentro;
      if (dentro) {
        caja.alpha = opacidad;
        caja.position.set(12, Math.round(y) - caja.height / 2);
      }
    }
  }

  // --- entrada ------------------------------------------------------------

  private conectarEntrada(): void {
    const c = this.app.canvas;

    c.addEventListener("pointerdown", (ev: PointerEvent) => {
      c.setPointerCapture(ev.pointerId);
      this.ultimoPuntero = { x: ev.offsetX, y: ev.offsetY };
      const tocado = this.nodoEn(ev.offsetX, ev.offsetY);
      if (tocado && ev.shiftKey) {
        // Shift + arrastrar mueve a esa persona. Sigue sin poder cambiar de
        // generacion: la fisica solo la mueve en horizontal.
        this.arrastrandoNodo = tocado;
        this.disposicion.fisica.fijar(
          tocado,
          this.camara.aMundo(ev.offsetX, ev.offsetY).x,
        );
        this.fisicaViva = true;
      } else {
        this.arrastrandoLienzo = true;
        this.camara.empezarArrastre();
      }
    });

    c.addEventListener("pointermove", (ev: PointerEvent) => {
      const dx = ev.offsetX - this.ultimoPuntero.x;
      const dy = ev.offsetY - this.ultimoPuntero.y;
      this.ultimoPuntero = { x: ev.offsetX, y: ev.offsetY };

      if (this.arrastrandoNodo) {
        this.disposicion.fisica.fijar(
          this.arrastrandoNodo,
          this.camara.aMundo(ev.offsetX, ev.offsetY).x,
        );
        this.fisicaViva = true;
        return;
      }
      if (this.arrastrandoLienzo) {
        this.camara.arrastrar(dx, dy);
        return;
      }
      this.resaltar(this.nodoEn(ev.offsetX, ev.offsetY));
    });

    const soltar = (ev: PointerEvent): void => {
      if (this.arrastrandoNodo) {
        this.disposicion.fisica.liberar(this.arrastrandoNodo);
        this.arrastrandoNodo = undefined;
        this.fisicaViva = true;
      } else if (this.arrastrandoLienzo) {
        this.camara.soltarArrastre();
        this.arrastrandoLienzo = false;
      }
      try {
        c.releasePointerCapture(ev.pointerId);
      } catch {
        // El puntero ya no esta capturado: no pasa nada.
      }
    };
    c.addEventListener("pointerup", soltar);
    c.addEventListener("pointercancel", soltar);

    c.addEventListener("pointerleave", () => this.resaltar(undefined));

    c.addEventListener("click", (ev: MouseEvent) => {
      const h = this.nodoEn(ev.offsetX, ev.offsetY);
      if (h) this.op.alSeleccionar?.(h, ev.ctrlKey || ev.metaKey);
      else this.op.alFondo?.();
    });

    c.addEventListener(
      "wheel",
      (ev: WheelEvent) => {
        ev.preventDefault();
        const factor = Math.exp(-ev.deltaY * 0.0016);
        this.camara.zoomEn(factor, ev.offsetX, ev.offsetY);
      },
      { passive: false },
    );
  }

  private alRedimensionar = (): void => {
    this.camara.redimensionar(this.app.screen.width, this.app.screen.height);
  };

  /**
   * Que persona hay en un punto de la pantalla.
   *
   * Se resuelve a mano y no con los eventos de Pixi: asi los nodos no
   * necesitan ser interactivos uno a uno, y el area sensible puede crecer
   * cuando se esta muy alejado, donde la tarjeta es un punto de 9 pixeles y
   * acertarle seria imposible.
   */
  nodoEn(px: number, py: number): string | undefined {
    const m = this.camara.aMundo(px, py);
    const esc = this.camara.escala;
    // Tolerancia: la tarjeta, o el punto minimo con holgura si esta pequena.
    const tolX = Math.max(ANCHO_NODO / 2, LADO_PUNTO_PANTALLA / esc);
    const tolY = Math.max(ALTO_NODO / 2, LADO_PUNTO_PANTALLA / esc);

    let mejor: string | undefined;
    let mejorDist = Number.POSITIVE_INFINITY;
    for (const n of this.disposicion.fisica.nodos.values()) {
      const ddx = Math.abs(m.x - n.x);
      const ddy = Math.abs(m.y - n.y);
      if (ddx > tolX || ddy > tolY) continue;
      const d = ddx + ddy;
      if (d < mejorDist) {
        mejorDist = d;
        mejor = n.handle;
      }
    }
    return mejor;
  }

  // --- resaltado y seleccion ---------------------------------------------

  /**
   * Resalta a una persona y sus conexiones, atenuando el resto.
   * Es solo opacidad: no mueve nada ni cambia ninguna posicion.
   */
  resaltar(handle: string | undefined): void {
    if (handle === this.resaltadoActual) return;
    this.resaltadoActual = handle;
    this.op.alPasar?.(handle);

    if (!handle) {
      this.atenuacionObjetivo = 1;
      for (const v of this.nodos.values()) v.alfaObjetivo = 1;
      this.enlaces.ponerResaltado(new Set());
      return;
    }

    const vecinos = this.ix.vecinos(handle);
    vecinos.add(handle);
    this.atenuacionObjetivo = ALFA_ATENUADO;
    for (const [h, v] of this.nodos) {
      v.alfaObjetivo = vecinos.has(h) ? 1 : ALFA_ATENUADO;
    }
    this.enlaces.ponerResaltado(vecinos);
  }

  ponerSeleccion(handles: Iterable<string>): void {
    const nueva = new Set(handles);
    for (const [h, v] of this.nodos) v.seleccionar(nueva.has(h));
    this.seleccion = nueva;
  }

  get seleccionados(): string[] {
    return [...this.seleccion];
  }

  // --- bucle --------------------------------------------------------------

  private cuadro(): void {
    const dt = this.app.ticker.deltaMS;
    this.tiempo += dt;
    this.cuadros++;

    this.camara.actualizar();

    // La fisica solo corre si hace falta, y se para sola.
    if (this.fisicaViva) {
      const e = pasoDisposicion(this.disposicion);
      for (const [h, v] of this.nodos) {
        const n = this.disposicion.fisica.nodos.get(h);
        if (n) v.mover(n.x, n.y);
      }
      this.enlaces.invalidar();
      if (e < ENERGIA_PARADA && !this.arrastrandoNodo) {
        this.fisicaViva = false;
        this.dibujarGuias();
      }
    }

    // Resaltado: se persigue con suavizado para que no parpadee.
    this.atenuacionActual += (this.atenuacionObjetivo - this.atenuacionActual) * SUAVIZADO_ALFA;

    const d = detalleDe(this.camara.escala);
    const esc = this.camara.escala;
    for (const v of this.nodos.values()) {
      if (Math.abs(v.alfaObjetivo - v.alfaActual) > 0.001) {
        v.alfaActual += (v.alfaObjetivo - v.alfaActual) * SUAVIZADO_ALFA;
      } else {
        v.alfaActual = v.alfaObjetivo;
      }
      const resp = Math.sin(this.tiempo * RESPIRACION_VEL + v.fase) * RESPIRACION;
      v.aplicar(d, esc, resp);
    }

    if (this.enlaces.redibujar(esc, this.atenuacionActual)) this.redibujos++;

    this.mundo.scale.set(esc);
    this.mundo.position.set(this.camara.x, this.camara.y);
    this.colocarEtiquetasFila();

    if (this.tiempo - this.ultimaMedida >= 1000) {
      const transcurrido = this.tiempo - this.ultimaMedida;
      this.op.alMedir?.({
        fps: (this.cuadros * 1000) / transcurrido,
        nodos: this.nodos.size,
        redibujosEnlaces: this.redibujos,
        fisicaViva: this.fisicaViva,
        escala: esc,
      });
      this.cuadros = 0;
      this.redibujos = 0;
      this.ultimaMedida = this.tiempo;
    }
  }

  /** Nombre legible, para los paneles. */
  nombreDe(handle: string): string {
    const p = this.arbol.personas.get(handle);
    return p ? nombreCompleto(p) : handle;
  }

  destruir(): void {
    window.removeEventListener("resize", this.alRedimensionar);
    for (const n of this.nodos.values()) n.destruir();
    this.nodos.clear();
    this.enlaces.destruir();
    this.app.destroy(true, { children: true });
  }
}
